import React, { useState, useEffect } from 'react';
import { 
  Modal, Descriptions, Tag, Spin, notification, Button,
  Tabs, Empty, Divider, Statistic, Card, Row, Col,
  Badge, List, Timeline, Typography, Alert, Space,
  Avatar
} from 'antd';
import {
  UserOutlined, PhoneOutlined, MailOutlined, HomeOutlined,
  BuildOutlined, BankOutlined, GlobalOutlined, TagOutlined,
  ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  EnvironmentOutlined, IdcardOutlined, TeamOutlined, ShoppingOutlined,
  DollarOutlined, FileTextOutlined, CalendarOutlined, UserSwitchOutlined,
  LockOutlined
} from '@ant-design/icons';
import userService from '../../../../services/userService';
import userImpersonationService from '../../../../services/userImpersonationService';
import agencyCustomerService from '../../../../services/agencyCustomerService';
import UserRolesPermissions from '../UserRolesPermissions';
import AgencyCustomersTab from '../AgencyCustomersTab';
import config from '../../../../config';

const { TabPane } = Tabs;
const { Title, Text, Paragraph } = Typography;

const getRoleNames = (user) => (
  Array.isArray(user?.roles)
    ? user.roles.map(role => (typeof role === 'string' ? role : role?.name)).filter(Boolean)
    : []
);

const isAgencyUser = (user) => {
  const roleNames = getRoleNames(user).map(roleName => String(roleName).toLowerCase());

  if (roleNames.includes('agency')) {
    return true;
  }

  if (roleNames.some(roleName => ['admin', 'superadmin', 'subadmin', 'developer'].includes(roleName))) {
    return false;
  }

  return String(user?.user_type) === '1';
};

/**
 * View User Modal Component
 * Updated to include agency customers view
 */
const ViewModal = ({ visible, userId, onCancel }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
   const [userStats, setUserStats] = useState({
    properties: 0,
    activeOrders: 0,
    completedOrders: 0,
    totalSpent: 0,
    lastOrder: null
  });
  const [managedCustomers, setManagedCustomers] = useState([]);
  const [managingAgencies, setManagingAgencies] = useState([]);
  const [loadingRelationships, setLoadingRelationships] = useState(false);
  
  // Fetch user data when modal opens
  useEffect(() => {
    if (visible && userId) {
      fetchUserData();
    }
    
    // Clear data when modal closes
    return () => {
      setUser(null);
      setUserStats({
        properties: 0,
        activeOrders: 0,
        completedOrders: 0,
        totalSpent: 0,
        lastOrder: null
      });
      setManagedCustomers([]);
      setManagingAgencies([]);
    };
  }, [visible, userId]);
  
  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await userService.getUserById(userId);
      setUser(response);
      
      // Fetch user stats
      try {
        const statsResponse = await userService.getUserStats(userId);
        setUserStats(statsResponse.data || {
          properties: 0,
          activeOrders: 0,
          completedOrders: 0,
          totalSpent: 0,
          lastOrder: null
        });
      } catch (error) {
        console.error('Error fetching user stats:', error);
      }

      // Fetch agency-customer relationships if needed
      if (isAgencyUser(response)) {
        // This is an agency, fetch managed customers
        fetchAgencyCustomers(userId);
      } else {
        // This is a customer, fetch managing agencies
        fetchCustomerAgencies(userId);
      }
    } catch (error) {
      notification.error({
        message: 'Error loading user',
        description: error.message || 'Failed to load user data'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch customers managed by this agency
  const fetchAgencyCustomers = async (agencyId) => {
    try {
      setLoadingRelationships(true);
      const response = await agencyCustomerService.getAgencyCustomers(agencyId);
      setManagedCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching managed customers:', error);
    } finally {
      setLoadingRelationships(false);
    }
  };

  // Fetch agencies managing this customer
  const fetchCustomerAgencies = async (customerId) => {
    try {
      setLoadingRelationships(true);
      const response = await agencyCustomerService.getCustomerAgencies(customerId);
      setManagingAgencies(response.data || []);
    } catch (error) {
      console.error('Error fetching managing agencies:', error);
    } finally {
      setLoadingRelationships(false);
    }
  };
  
  const handleImpersonateUser = async () => {
    try {
      const response = await userImpersonationService.impersonateUser(userId);
      if (response.success) {
        notification.success({
          message: 'Impersonation successful',
          description: 'You are now logged in as the selected user'
        });
        // Close the modal
        onCancel();
        // Redirect to dashboard or reload to apply new user session
        window.location.href = '/';
      } else {
        notification.error({
          message: 'Impersonation failed',
          description: response.message || 'Failed to impersonate user'
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error impersonating user',
        description: error.message || 'Failed to impersonate user'
      });
    }
  };
  
  const getUserTypeTag = (userType) => {
    if (userType === '1' || userType === 1) {
      return <Tag color="green">Agency</Tag>;
    } else if (userType === '0' || userType === 0) {
      return <Tag color="blue">Customer</Tag>;
    } else {
      return <Tag color="orange">Other</Tag>;
    }
  };
  
  const getFormattedDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Check if user is an agency
  const isAgency = isAgencyUser(user);
  
  return (
    <Modal
      title="User Details"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button 
          key="impersonate" 
          type="primary" 
          icon={<UserSwitchOutlined />} 
          onClick={handleImpersonateUser}
          className="bg-green-600 hover:bg-green-700"
        >
          Impersonate User
        </Button>,
        <Button key="back" onClick={onCancel}>
          Close
        </Button>
      ]}
    >
      <Spin spinning={loading}>
        {user ? (
          <Tabs defaultActiveKey="overview">
            <TabPane 
              tab={<span><UserOutlined /> Overview</span>} 
              key="overview"
            >
              <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-1/3">
                  <Card className="mb-4">
                    <div className="text-center">
                      <div className="mx-auto bg-gray-200 rounded-full w-20 h-20 flex items-center justify-center mb-4">
                        <UserOutlined style={{ fontSize: 40, color: '#666' }} />
                      </div>
                      <Title level={4}>{user.name}</Title>
                      <div className="flex justify-center">
                        {getUserTypeTag(user.user_type)}
                        <Tag color={user.status === 1 || user.status === '1' ? 'green' : 'red'}>
                          {user.status === 1 || user.status === '1' ? 'Active' : 'Inactive'}
                        </Tag>
                      </div>
                      <Divider />
                      <div className="text-left">
                        <p className="flex items-center">
                          <MailOutlined className="mr-2" /> {user.email}
                        </p>
                        {user.secondary_email && (
                          <p className="flex items-center">
                            <MailOutlined className="mr-2" /> {user.secondary_email} (Secondary)
                          </p>
                        )}
                        {user.phone && (
                          <p className="flex items-center">
                            <PhoneOutlined className="mr-2" /> {user.phone}
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                  
                  <Card title="User Activity" className="mb-4">
                    <Statistic
                      title="Account Created"
                      value={getFormattedDate(user.created_at)}
                      prefix={<CalendarOutlined />}
                    />
                    <Divider />
                    <Statistic
                      title="Last Login"
                      value={getFormattedDate(user.last_login) || 'Never'}
                      prefix={<ClockCircleOutlined />}
                    />
                  </Card>
                </div>
                
                <div className="md:w-2/3">
                  <Card title="User Statistics" className="mb-4">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Properties"
                          value={userStats.properties || 0}
                          prefix={<HomeOutlined />}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Active Orders"
                          value={userStats.activeOrders || 0}
                          prefix={<ShoppingOutlined />}
                        />
                      </Col>
                    </Row>
                    <Divider />
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Completed Orders"
                          value={userStats.completedOrders || 0}
                          prefix={<CheckCircleOutlined />}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Total Spent"
                          value={userStats.totalSpent ? `${parseFloat(userStats.totalSpent).toFixed(2)}` : '£0.00'}
                          prefix={<DollarOutlined />}
                        />
                      </Col>
                    </Row>
                    <Divider />
                    <div>
                      <Text strong>Last Order:</Text> {userStats.lastOrder ? getFormattedDate(userStats.lastOrder) : 'No orders yet'}
                    </div>
                  </Card>
                  
                  {/* Show agency-customer relationships */}
                  {(isAgency || managingAgencies.length > 0) && (
                    <Card 
                      title={
                        <div className="flex items-center">
                          <TeamOutlined className="mr-2" />
                          {isAgency ? "Managed Customers" : "Managing Agencies"}
                        </div>
                      } 
                      className="mb-4"
                    >
                      <Spin spinning={loadingRelationships}>
                        {isAgency ? (
                          // Show customers managed by this agency
                          managedCustomers.length > 0 ? (
                            <List
                              itemLayout="horizontal"
                              dataSource={managedCustomers}
                              renderItem={item => (
                                <List.Item>
                                  <List.Item.Meta
                                    avatar={<Avatar icon={<UserOutlined />} />}
                                    title={<Text strong>{item.name}</Text>}
                                    description={
                                      <div>
                                        <div>{item.email}</div>
                                        {item.phone && <div>{item.phone}</div>}
                                      </div>
                                    }
                                  />
                                </List.Item>
                              )}
                            />
                          ) : (
                            <Empty description="No customers assigned to this agency yet" />
                          )
                        ) : (
                          // Show agencies managing this customer
                          managingAgencies.length > 0 ? (
                            <List
                              itemLayout="horizontal"
                              dataSource={managingAgencies}
                              renderItem={item => (
                                <List.Item>
                                  <List.Item.Meta
                                    avatar={<Avatar icon={<TeamOutlined />} />}
                                    title={<Text strong>{item.name}</Text>}
                                    description={
                                      <div>
                                        <div>{item.email}</div>
                                        {item.phone && <div>{item.phone}</div>}
                                      </div>
                                    }
                                  />
                                </List.Item>
                              )}
                            />
                          ) : (
                            <Empty description="No agencies are managing this customer" />
                          )
                        )}
                      </Spin>
                    </Card>
                  )}
                  
                  {user.account_type !== 'private' && user.company_name && (
                    <Card title="Company Information" className="mb-4">
                      <Descriptions column={1} bordered>
                        <Descriptions.Item label="Company Name">
                          <span className="flex items-center">
                            <BuildOutlined className="mr-2" /> {user.company_name}
                          </span>
                        </Descriptions.Item>
                        {user.company_registration && (
                          <Descriptions.Item label="Registration Number">
                            <span className="flex items-center">
                              <IdcardOutlined className="mr-2" /> {user.company_registration}
                            </span>
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Card>
                  )}
                  
                  <Card title="Address Information" className="mb-4">
                    {user.apartment_number || user.address || user.city || user.post_code ? (
                      <Descriptions column={1} bordered>
                        {user.apartment_number && (
                          <Descriptions.Item label="Apartment/Building Number">
                            {user.apartment_number}
                          </Descriptions.Item>
                        )}
                        {user.address && (
                          <Descriptions.Item label="Address">
                            {user.address}
                          </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Location">
                          <span className="flex items-center">
                            <EnvironmentOutlined className="mr-2" />
                            {user.city ? `${user.city}, ` : ''}
                            {user.post_code || 'No postal code'}
                            {user.country ? ` (${user.country})` : ''}
                          </span>
                        </Descriptions.Item>
                      </Descriptions>
                    ) : (
                      <Empty description="No address information available" />
                    )}
                  </Card>
                </div>
              </div>
            </TabPane>
            
            <TabPane 
              tab={<span><FileTextOutlined /> Service Information</span>} 
              key="service"
            >
              <Card>
                <Descriptions title="Service Details" bordered column={2}>
                  <Descriptions.Item label="Service Type">
                    <Tag color="blue">{user.service_type || 'LinenHire'}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Property Range">
                    {user.property_range_id ? `Range ID: ${user.property_range_id}` : 'Not set'}
                  </Descriptions.Item>
                  <Descriptions.Item label="User Type">
                    {getUserTypeTag(user.user_type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Account Type">
                    <Tag color={user.account_type === 'company' ? 'blue' : 'default'}>
                      {user.account_type === 'company' ? 'Company' : 'Private'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Badge 
                      status={user.status === 1 || user.status === '1' ? 'success' : 'error'} 
                      text={user.status === 1 || user.status === '1' ? 'Active' : 'Inactive'} 
                    />
                  </Descriptions.Item>
                </Descriptions>
                
                <Divider />
                
                <div className="mb-4">
                  <Alert
                    message="User Impersonation"
                    description="You can impersonate this user to view the system from their perspective. This is useful for troubleshooting issues they may be experiencing."
                    type="info"
                    showIcon
                    action={
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={handleImpersonateUser}
                        icon={<UserSwitchOutlined />}
                      >
                        Impersonate
                      </Button>
                    }
                  />
                </div>
                
                <Title level={4}>Recent Activity</Title>
                <Timeline mode="left">
                  <Timeline.Item>Account created on {getFormattedDate(user.created_at)}</Timeline.Item>
                  {user.last_login && (
                    <Timeline.Item>Last login on {getFormattedDate(user.last_login)}</Timeline.Item>
                  )}
                  {user.updated_at && user.updated_at !== user.created_at && (
                    <Timeline.Item>Profile updated on {getFormattedDate(user.updated_at)}</Timeline.Item>
                  )}
                </Timeline>
              </Card>
            </TabPane>
            
            <TabPane
              tab={<span><LockOutlined /> Roles & Permissions</span>}
              key="roles-permissions"
            >
              <UserRolesPermissions 
                userId={userId}
                viewOnly={true}
                userName={user.name || `${user.first_name || ''} ${user.last_name || ''}`}
              />
            </TabPane>
            
            {/* Show agency customers tab for agencies */}
            {isAgency && (
              <TabPane
                tab={<span><TeamOutlined /> Managed Customers</span>}
                key="managed-customers"
              >
                <AgencyCustomersTab 
                  agencyId={userId}
                  readOnly={true}
                />
              </TabPane>
            )}
          </Tabs>
        ) : (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
            <p className="ml-3 text-gray-500">Loading user data...</p>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ViewModal;
